/**
 * 高级检索工具
 * 交互对齐 Excel / 表格筛选：检索项 + 条件 + 检索词；多行之间默认「同时满足」
 */

export const FALLBACK_BOOL_CLAUSES = [
  { value: 'must', label: '必须' },
  { value: 'filter', label: '过滤' },
  { value: 'should', label: '可选' },
  { value: 'must_not', label: '排除' },
]

/** 正向条件文案 */
const OPERATOR_INCLUDE_LABELS = {
  match: '包含',
  match_phrase: '精确短语',
  term: '等于',
  terms: '等于任一',
  range: '介于',
  exists: '有值',
  prefix: '开头是',
  wildcard: '通配',
}

/** 反向条件文案（对应 must_not） */
const OPERATOR_EXCLUDE_LABELS = {
  match: '不包含',
  match_phrase: '不含短语',
  term: '不等于',
  terms: '不等于任一',
  prefix: '开头不是',
  wildcard: '不符合通配',
}

function stripEsPrefix(label) {
  const text = String(label || '').trim()
  if (!text) return ''
  const m = text.match(/^[a-z_]+\s+(.+)$/i)
  return m ? m[1].trim() : text
}

export function getFieldMeta(config, fieldName) {
  return (config?.fields || []).find((f) => f.field === fieldName) || null
}

export function getMatchModeOptions(config, fieldName) {
  const meta = getFieldMeta(config, fieldName)
  return (meta?.match_modes || []).map((m) => ({
    ...m,
    label: OPERATOR_INCLUDE_LABELS[m.value] || stripEsPrefix(m.label) || m.value,
  }))
}

/**
 * Excel 风格「条件」下拉：把 match_mode + 是否排除 合成一项
 * value 形如 match|in / term|out
 */
export function getOperatorOptions(config, fieldName) {
  const meta = getFieldMeta(config, fieldName)
  const modes = meta?.match_modes || []
  const canExclude = (meta?.clauses || ['must', 'filter', 'must_not']).includes('must_not')
  const options = []

  modes.forEach((m) => {
    const includeLabel = OPERATOR_INCLUDE_LABELS[m.value] || stripEsPrefix(m.label) || m.value
    options.push({
      value: `${m.value}|in`,
      label: includeLabel,
      match_mode: m.value,
      exclude: false,
    })
    if (canExclude && OPERATOR_EXCLUDE_LABELS[m.value]) {
      options.push({
        value: `${m.value}|out`,
        label: OPERATOR_EXCLUDE_LABELS[m.value],
        match_mode: m.value,
        exclude: true,
      })
    }
  })

  return options
}

export function rowToOperatorValue(row) {
  const exclude = row.clause === 'must_not'
  return `${row.match_mode || 'match'}|${exclude ? 'out' : 'in'}`
}

export function applyOperatorToRow(row, operatorValue, config) {
  const [matchMode, dir] = String(operatorValue || 'match|in').split('|')
  row.match_mode = matchMode || 'match'
  if (dir === 'out') {
    row.clause = 'must_not'
  } else {
    row.clause = defaultClause(config, row.field, row.match_mode)
  }
  if (row.match_mode !== 'range') row.valueEnd = ''
}

export function defaultMatchMode(config, fieldName) {
  const modes = getMatchModeOptions(config, fieldName)
  return modes[0]?.value || 'match'
}

export function defaultClause(config, fieldName, matchMode) {
  const meta = getFieldMeta(config, fieldName)
  const allowed = meta?.clauses || ['must', 'filter', 'must_not']
  const preferFilter = ['term', 'terms', 'range'].includes(matchMode)
  if (preferFilter && allowed.includes('filter')) return 'filter'
  if (allowed.includes('must')) return 'must'
  if (allowed.includes('filter')) return 'filter'
  return allowed[0] || 'must'
}

export function getClauseOptions(config, fieldName) {
  const meta = getFieldMeta(config, fieldName)
  const allowed = meta?.clauses || ['must', 'filter', 'must_not']
  const all = config?.bool_clauses || FALLBACK_BOOL_CLAUSES
  const labelMap = {
    must: '必须',
    filter: '过滤',
    should: '可选',
    must_not: '排除',
  }
  return all
    .filter((c) => allowed.includes(c.value))
    .map((c) => ({
      ...c,
      label: labelMap[c.value] || stripEsPrefix(c.label) || c.value,
    }))
}

export function createDefaultRows(config, maxRows = 3) {
  const fields = config?.fields || []
  return fields.slice(0, maxRows).map((f) => {
    const matchMode = defaultMatchMode(config, f.field)
    return {
      field: f.field,
      match_mode: matchMode,
      clause: defaultClause(config, f.field, matchMode),
      value: usesArrayValue(config, f.field, matchMode) ? [] : '',
      valueEnd: '',
    }
  })
}

export function createEmptyRow(config) {
  const first = config?.fields?.[0]
  if (!first) {
    return { field: '', match_mode: 'match', clause: 'must', value: '', valueEnd: '' }
  }
  const matchMode = defaultMatchMode(config, first.field)
  return {
    field: first.field,
    match_mode: matchMode,
    clause: defaultClause(config, first.field, matchMode),
    value: usesArrayValue(config, first.field, matchMode) ? [] : '',
    valueEnd: '',
  }
}

export function parseRangeValue(raw, rawEnd, { isDate = false } = {}) {
  const v = (raw || '').trim()
  if (!v && !(rawEnd || '').trim()) return null
  if (v.startsWith('{')) {
    try {
      return JSON.parse(v)
    } catch (e) {
      /* fall through */
    }
  }
  if (v.includes('..')) {
    const [gte, lte] = v.split('..')
    const obj = {}
    if (gte?.trim()) obj.gte = isDate ? toDateApiValue(gte, 'start') : gte.trim()
    if (lte?.trim()) obj.lte = isDate ? toDateApiValue(lte, 'end') : lte.trim()
    return obj
  }
  const end = (rawEnd || '').trim()
  const obj = {}
  if (v) obj.gte = isDate ? toDateApiValue(v, 'start') : v
  if (end) obj.lte = isDate ? toDateApiValue(end, 'end') : end
  return Object.keys(obj).length ? obj : null
}

export function buildConditionValue(row, config) {
  const isDate = isDateField(config, row.field)
  if (row.match_mode === 'range') {
    return parseRangeValue(row.value, row.valueEnd, { isDate })
  }
  if (row.match_mode === 'terms') {
    if (Array.isArray(row.value)) {
      return row.value.map((s) => String(s).trim()).filter(Boolean)
    }
    return String(row.value || '')
      .split(/[,，]/)
      .map((s) => s.trim())
      .filter(Boolean)
  }
  if (row.match_mode === 'exists') return true
  if (Array.isArray(row.value)) {
    const list = row.value.map((s) => String(s).trim()).filter(Boolean)
    if (!list.length) return null
    return list.length === 1 ? list[0] : list
  }
  if (isDate) {
    return toDateApiValue(row.value, 'start') || null
  }
  return row.value
}

export function buildAdvancedSearchBody(rows, { page = 1, limit = 20, config = null } = {}) {
  const conditions = (rows || [])
    .filter((r) => r.field && r.match_mode)
    .filter((r) => {
      if (r.match_mode === 'exists') return true
      if (r.match_mode === 'range') {
        return !!(String(r.value || '').trim() || String(r.valueEnd || '').trim())
      }
      if (Array.isArray(r.value)) return r.value.some((v) => String(v ?? '').trim() !== '')
      return String(r.value || '').trim() !== ''
    })
    .map((r) => ({
      field: r.field,
      clause: r.clause || 'must',
      match_mode: r.match_mode,
      value: buildConditionValue(r, config),
    }))
    .filter((c) => {
      if (c.value === null || c.value === undefined || c.value === '') return false
      if (Array.isArray(c.value) && !c.value.length) return false
      return true
    })

  return {
    advance_query: 1,
    page,
    limit,
    conditions,
  }
}

export function getValuePlaceholder(row, config) {
  const meta = getFieldMeta(config, row.field)
  if (meta?.placeholder) return meta.placeholder
  if (isTreeField(config, row.field)) return '请选择（可多选）'
  if (isSelectField(config, row.field)) {
    return isMultiValueSelect(config, row.field, row.match_mode) ? '请选择（可多选）' : '请选择'
  }
  if (row.match_mode === 'range') return '起始值'
  if (row.match_mode === 'terms') return '多个值用逗号分隔'
  if (row.match_mode === 'exists') return '无需填写'
  return '请输入'
}

/** 取日期部分 YYYY-MM-DD */
export function normalizeDateYmd(raw) {
  const s = String(raw || '').trim()
  if (!s) return ''
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/)
  return m ? m[1] : ''
}

/**
 * 日期检索值：YYYY-MM-DDTHH:mm:ss
 * start → 00:00:00，end → 23:59:59
 */
export function toDateApiValue(raw, bound = 'start') {
  const day = normalizeDateYmd(raw)
  if (!day) return ''
  return bound === 'end' ? `${day}T23:59:59` : `${day}T00:00:00`
}

export function isDateField(config, fieldName) {
  const meta = getFieldMeta(config, fieldName)
  return meta?.widget === 'date_range' || meta?.widget === 'date' || meta?.es_type === 'date'
}

export function isDateRangeField(config, fieldName) {
  return isDateField(config, fieldName)
}

/** 将配置 / 普通筛选项统一成 { label, value } */
export function normalizeFieldOptions(rawOptions) {
  if (!Array.isArray(rawOptions) || !rawOptions.length) return []
  return rawOptions
    .map((item, index) => {
      if (item == null || item === '') return null
      if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
        const value = String(item)
        return { label: value, value }
      }
      if (typeof item !== 'object') return null
      const value = item.value ?? item.option_value ?? item.id ?? item.option_id ?? item.key
      const label = item.label ?? item.option_name ?? item.name ?? item.title ?? value
      if (value === undefined || value === null || value === '') return null
      return {
        label: String(label ?? value),
        value: typeof value === 'string' || typeof value === 'number' ? value : String(value),
        key: item.option_id ?? item.id ?? `${value}-${index}`,
      }
    })
    .filter(Boolean)
}

export function getFieldSelectOptions(config, fieldName) {
  const meta = getFieldMeta(config, fieldName)
  if (isTreeField(config, fieldName)) return []
  return normalizeFieldOptions(meta?.options)
}

/** 选项是否为树结构（含 children） */
export function looksLikeTreeOptions(rawOptions) {
  if (!Array.isArray(rawOptions) || !rawOptions.length) return false
  const walk = (nodes) => {
    for (const node of nodes) {
      if (!node || typeof node !== 'object') continue
      if (Array.isArray(node.children) && node.children.length) return true
    }
    return false
  }
  return walk(rawOptions)
}

export function getFieldTreeOptions(config, fieldName) {
  const meta = getFieldMeta(config, fieldName)
  const options = meta?.options
  return Array.isArray(options) ? options : []
}

/** 树形：widget=tree，或 options 带层级 children */
export function isTreeField(config, fieldName) {
  const meta = getFieldMeta(config, fieldName)
  if (!meta || isDateField(config, fieldName)) return false
  const widget = String(meta.widget || '').toLowerCase()
  if (widget === 'tree') return true
  return looksLikeTreeOptions(meta.options)
}

/** 下拉：widget 为 select/option/radio，或配置里带了扁平 options（非树） */
export function isSelectField(config, fieldName) {
  const meta = getFieldMeta(config, fieldName)
  if (!meta || isDateField(config, fieldName) || isTreeField(config, fieldName)) return false
  const widget = String(meta.widget || '').toLowerCase()
  if (['select', 'option', 'radio', 'tags', 'tag'].includes(widget)) return true
  return getFieldSelectOptions(config, fieldName).length > 0
}

export function isMultiValueSelect(config, fieldName, matchMode) {
  if (!isSelectField(config, fieldName)) return false
  const meta = getFieldMeta(config, fieldName)
  if (matchMode === 'terms') return true
  if (matchMode === 'term' || matchMode === 'exists') return false
  return !!meta?.allow_multiple
}

/** 树 / 多选下拉：值用数组 */
export function usesArrayValue(config, fieldName, matchMode) {
  return isTreeField(config, fieldName) || isMultiValueSelect(config, fieldName, matchMode)
}

/**
 * 用普通筛选 filter-options 补齐高级检索字段的下拉/树选项
 * （高级检索配置未带 options / widget 时与普通筛选保持一致）
 */
export function enrichAdvancedSearchConfigWithFilters(config, filters) {
  if (!config?.fields?.length || !Array.isArray(filters) || !filters.length) {
    return config
  }
  const filterMap = new Map(
    filters
      .filter((f) => f?.field_name)
      .map((f) => [String(f.field_name), f]),
  )
  return {
    ...config,
    fields: config.fields.map((field) => {
      const filter = filterMap.get(String(field.field))
      if (!filter) {
        // 配置自身已是树结构时标成 tree
        if (looksLikeTreeOptions(field.options) && !field.widget) {
          return { ...field, widget: 'tree', allow_multiple: true }
        }
        return field
      }

      const filterType = String(filter.search_type || '').toLowerCase()
      const widgetRaw = String(field.widget || '').toLowerCase()
      const filterOptions = Array.isArray(filter.options) ? filter.options : []
      const fieldOptions = Array.isArray(field.options) ? field.options : []

      // 树形：保留层级结构，不要 flatten
      if (
        filterType === 'tree'
        || widgetRaw === 'tree'
        || looksLikeTreeOptions(fieldOptions)
        || looksLikeTreeOptions(filterOptions)
      ) {
        const options = fieldOptions.length ? fieldOptions : filterOptions
        if (!options.length) return field
        return {
          ...field,
          widget: 'tree',
          options,
          allow_multiple: true,
        }
      }

      const existing = normalizeFieldOptions(fieldOptions)
      const fromFilter = normalizeFieldOptions(filterOptions)
      const options = existing.length ? existing : fromFilter
      if (!options.length) return field

      let widget = field.widget
      if (!widgetRaw || widgetRaw === 'input') {
        if (filterType === 'option' || filterType === 'radio') {
          widget = 'select'
        } else if (!widgetRaw && options.length) {
          widget = 'select'
        }
      }

      return {
        ...field,
        widget,
        options,
        allow_multiple: field.allow_multiple ?? (filterType === 'option'),
      }
    }),
  }
}

function normalizeSelectRowValue(row, config) {
  if (isTreeField(config, row.field)) {
    if (!Array.isArray(row.value)) {
      const text = String(row.value || '').trim()
      row.value = text
        ? text.split(/[,，]/).map((s) => s.trim()).filter(Boolean)
        : []
    }
    return
  }
  if (!isSelectField(config, row.field)) return
  if (isMultiValueSelect(config, row.field, row.match_mode)) {
    if (Array.isArray(row.value)) return
    const text = String(row.value || '').trim()
    row.value = text
      ? text.split(/[,，]/).map((s) => s.trim()).filter(Boolean)
      : []
    return
  }
  if (Array.isArray(row.value)) {
    row.value = row.value.length ? row.value[0] : ''
  }
}

export function normalizeSearchEndpoint(endpoint) {
  if (!endpoint) return ''
  const m = endpoint.match(/\/api\/v1\/(.+)$/)
  if (m) return `/v1/${m[1]}`
  if (endpoint.startsWith('/v1/')) return endpoint
  return endpoint
}

export const apiMatchTypeOptions = [
  { value: 'match', label: '包含' },
  { value: 'match_phrase', label: '精确短语' },
  { value: 'term', label: '等于' },
  { value: 'terms', label: '等于任一' },
  { value: 'range', label: '介于' },
]

export const apiClauseOptions = FALLBACK_BOOL_CLAUSES

export function onFieldChange(row, config) {
  row.match_mode = defaultMatchMode(config, row.field)
  row.clause = defaultClause(config, row.field, row.match_mode)
  row.value = usesArrayValue(config, row.field, row.match_mode) ? [] : ''
  row.valueEnd = ''
}

export function onMatchModeChange(row, config) {
  if (row.clause !== 'must_not') {
    row.clause = defaultClause(config, row.field, row.match_mode)
  }
  if (row.match_mode === 'range') {
    row.valueEnd = row.valueEnd || ''
  } else {
    row.valueEnd = ''
  }
  normalizeSelectRowValue(row, config)
}

export function formatConfigFields(config) {
  return (config?.fields || []).map((f) => ({
    field: f.field,
    label: f.label,
    es_type: f.es_type,
    widget: f.widget,
    placeholder: f.placeholder,
    allow_multiple: f.allow_multiple,
  }))
}

/** @deprecated 兼容旧调用 */
export function friendlyMatchModeLabel(value, fallbackLabel) {
  return OPERATOR_INCLUDE_LABELS[value] || stripEsPrefix(fallbackLabel) || value
}

export function getLogicOptions() {
  return []
}

export function clauseToLogic(clause) {
  return clause === 'must_not' ? 'not' : 'and'
}

export function logicToClause(config, fieldName, matchMode, logic) {
  if (logic === 'not') return 'must_not'
  return defaultClause(config, fieldName, matchMode)
}
